/**
 * @file Deposit functionality for Nucleus vaults
 * @module vaults/deposit
 */

import { type Address, erc20Abi, parseUnits } from 'viem';
import { getRateInQuoteWithAssetDecimals } from '../../api/accountant';
import { getErc20Decimals } from '../../api/erc20';
import { getPreviewFee, type BridgeData } from '../../api/teller';
import { type ChainId, toChainId } from '../../api/vault-config';
import {
  DEFAULT_APPROVAL_AMOUNT,
  DEFAULT_DEPOSIT_SLIPPAGE,
} from '../../constants';
import { TellerAbi } from '../../contracts/teller-abi';
import { tryCatch } from '../../utils/try-catch';
import { prepareBridgeContractArg } from '../bridge';
import type { VaultKey } from '../config';
import { getVaultByKey } from '../index';
import type { TokenKey } from '../tokens';
import { calculateMinimumMint } from './utils';

/**
 * Parameters required for preparing a deposit transaction
 * @interface PrepareDepositTransactionDataParams
 * @property {VaultKey} vaultKey - Unique identifier for the target vault
 * @property {Address} userAddress - Ethereum address of the user making the deposit
 * @property {string} depositTokenSymbol - Symbol of the token being deposited (e.g., 'WETH', 'USDC')
 * @property {string} depositAmount - Amount of assets to deposit as a decimal string (e.g., "1.5")
 * @property {number | string} chainId - ID of the chain where the deposit will occur
 * @property {number} [slippage] - Maximum acceptable slippage percentage as a decimal (e.g., 0.01 for 1%)
 */
interface PrepareDepositTransactionDataParams {
  vaultKey: VaultKey;
  userAddress: Address;
  depositTokenSymbol: TokenKey;
  depositAmount: string;
  chainId: ChainId;
  slippage?: number;
}

/**
 * Result object containing transaction data for a deposit operation
 * @interface DepositTransactionData
 * @property {typeof TellerAbi} abi - ABI for the Teller contract
 * @property {Address} address - Address of the Teller contract
 * @property {'deposit'} functionName - Name of the function to call
 * @property {[Address, bigint, bigint]} args - Arguments for the deposit function:
 *   [depositAsset, depositAmount, minimumMint]
 * @property {number} chainId - ID of the chain where the transaction should be executed
 */
interface DepositTransactionData {
  abi: typeof TellerAbi;
  address: Address;
  functionName: 'deposit';
  args: [Address, bigint, bigint];
  chainId: number;
}

/**
 * Prepares the transaction data needed to deposit assets into a vault
 *
 * This function calculates the minimum amount of vault tokens to be minted based on
 * the current exchange rate and the specified slippage tolerance. It returns a transaction
 * object that can be used with viem or ethers.js to execute the deposit.
 *
 * @example
 * ```typescript
 * const depositData = await prepareDepositData({
 *   vaultKey: 'bobaeth',
 *   userAddress: '0x1234...',
 *   depositTokenSymbol: 'WETH',
 *   depositAmount: "1.0", // 1 WETH
 *   chainId: 1,
 *   slippage: 0.01, // 1% slippage
 * });
 * ```
 *
 * @param {PrepareDepositTransactionDataParams} params - Parameters for the deposit operation
 * @param {VaultKey} params.vaultKey - Unique identifier for the vault
 * @param {Address} params.userAddress - Ethereum address of the user making the deposit
 * @param {string} params.depositTokenSymbol - Symbol of the token being deposited
 * @param {string} params.depositAmount - Amount of assets to deposit as a string
 * @param {number | string} params.chainId - ID of the chain where the deposit will occur
 * @param {number} [params.slippage=DEFAULT_DEPOSIT_SLIPPAGE] - Maximum acceptable slippage percentage
 *
 * @returns {Promise<DepositTransactionData>} Promise resolving to the prepared transaction data
 * @throws {Error} If the vault key is invalid or if contracts are not properly configured
 */
const prepareDepositTransactionData = async ({
  vaultKey,
  depositTokenSymbol,
  depositAmount,
  chainId,
  slippage = DEFAULT_DEPOSIT_SLIPPAGE,
}: PrepareDepositTransactionDataParams): Promise<DepositTransactionData> => {
  try {
    const normalizedChainId = toChainId(chainId);
    const vault = await getVaultByKey(vaultKey);

    const depositAssetAddress =
      vault.deposit.sourceChains[normalizedChainId].depositTokens[
        depositTokenSymbol
      ].address;

    if (!vault) {
      throw new Error(`Invalid vault key: ${vaultKey}`);
    }
    if (!depositAssetAddress) {
      throw new Error(
        `Couldn't find address for ${depositTokenSymbol} on ${chainId}`
      );
    }

    if (!vault.contracts) {
      throw new Error(`Contracts not configured for vault ${vaultKey}`);
    }

    if (!vault.contracts.teller) {
      throw new Error(`Teller contract not configured for vault ${vaultKey}`);
    }

    const [depositAssetDecimals, rate] = await getRateInQuoteWithAssetDecimals({
      assetAddress: depositAssetAddress,
      accountantAddress: vault.contracts.accountant,
      chainId: normalizedChainId,
    });

    if (depositAssetDecimals.status === 'failure') {
      throw new Error(depositAssetDecimals.error.message);
    }

    if (rate.status === 'failure') {
      throw new Error(rate.error.message);
    }

    const depositAmountAsBitInt = parseUnits(
      depositAmount,
      depositAssetDecimals.result
    );

    const minimumMint = calculateMinimumMint(
      depositAmountAsBitInt,
      rate.result,
      slippage
    );

    return {
      abi: TellerAbi,
      address: vault.contracts.teller,
      functionName: 'deposit',
      args: [depositAssetAddress, depositAmountAsBitInt, minimumMint],
      chainId: normalizedChainId,
    };
  } catch (error) {
    console.error(error);
    throw new Error(`Failed to get rate in quote for vault ${vaultKey}`);
  }
};

/**
 * Parameters required for preparing a deposit and bridge transaction
 * @interface PrepareDepositAndBridgeTransactionDataParams
 * @property {VaultKey} vaultKey - Unique identifier for the target vault
 * @property {string} depositTokenSymbol - Symbol of the token being deposited
 * @property {string} depositAmount - Amount of assets to deposit as a string
 * @property {number | string} sourceChainId - ID of the chain where the deposit originates
 * @property {number | string} destinationChainId - ID of the chain where tokens will be bridged to
 * @property {Address} userAddress - Ethereum address of the user making the deposit
 * @property {number} [slippage] - Maximum acceptable slippage percentage (defaults to DEFAULT_DEPOSIT_SLIPPAGE)
 */
interface PrepareDepositAndBridgeTransactionDataParams {
  depositAmount: string;
  depositTokenSymbol: TokenKey;
  destinationChainId: ChainId;
  slippage?: number;
  sourceChainId: ChainId;
  userAddress: Address;
  vaultKey: VaultKey;
}

/**
 * Result object containing transaction data for a deposit and bridge operation
 * @interface DepositAndBridgeTransactionData
 * @property {typeof TellerAbi} abi - ABI for the Teller contract
 * @property {Address} address - Address of the Teller contract
 * @property {'depositAndBridge'} functionName - Name of the function to call
 * @property {[Address, bigint, bigint, BridgeData]} args - Arguments for the depositAndBridge function:
 *   - depositAsset: Address of the token being deposited
 *   - depositAmount: Amount of tokens to deposit in base units (e.g., wei)
 *   - minimumMint: Minimum amount of vault tokens to receive after slippage
 *   - bridgeData: Data required for cross-chain bridging
 * @property {number} chainId - ID of the chain where the transaction should be executed
 * @property {bigint} value - Amount of native token to send with the transaction (for bridge fees)
 */
interface DepositAndBridgeTransactionData {
  abi: typeof TellerAbi;
  address: Address;
  functionName: 'depositAndBridge';
  args: [Address, bigint, bigint, BridgeData];
  chainId: number;
  value: bigint;
}

/**
 * Prepares the transaction data needed to deposit assets into a vault and bridge them to another chain
 *
 * This function calculates the minimum amount of vault tokens to be minted based on
 * the current exchange rate and the specified slippage tolerance, then prepares the bridge data
 * for cross-chain transfer. It returns a transaction object that can be used with viem or ethers.js
 * to execute the deposit and bridge operation.
 *
 * @example
 * ```typescript
 * const depositAndBridgeData = await prepareDepositAndBridgeData({
 *   vaultKey: 'bobaeth',
 *   depositTokenSymbol: 'WETH',
 *   depositAmount: "1.0", // 1 WETH
 *   sourceChainId: 1,
 *   destinationChainId: 10,
 *   userAddress: '0x1234...',
 *   slippage: 0.01, // 1% slippage
 * });
 * ```
 *
 * @param {PrepareDepositAndBridgeTransactionDataParams} params - Parameters for the deposit and bridge operation
 * @param {VaultKey} params.vaultKey - Unique identifier for the vault
 * @param {string} params.depositTokenSymbol - Symbol of the token being deposited
 * @param {string} params.depositAmount - Amount of assets to deposit as a string
 * @param {number | string} params.sourceChainId - ID of the chain where the deposit originates
 * @param {number | string} params.destinationChainId - ID of the chain where tokens will be bridged to
 * @param {Address} params.userAddress - Ethereum address of the user making the deposit
 * @param {number} [params.slippage=DEFAULT_DEPOSIT_SLIPPAGE] - Maximum acceptable slippage percentage
 *
 * @returns {Promise<DepositAndBridgeTransactionData>} Promise resolving to the prepared transaction data
 * @throws {Error} If the vault key is invalid or if contracts are not properly configured
 */
const prepareDepositAndBridgeTransactionData = async ({
  depositAmount,
  depositTokenSymbol,
  destinationChainId,
  slippage = DEFAULT_DEPOSIT_SLIPPAGE,
  sourceChainId,
  userAddress,
  vaultKey,
}: PrepareDepositAndBridgeTransactionDataParams): Promise<DepositAndBridgeTransactionData> => {
  const normalizedSourceChainId = toChainId(sourceChainId);
  const normalizedDestinationChainId = toChainId(destinationChainId);
  const vault = await getVaultByKey(vaultKey);
  const depositAssetAddress =
    vault.deposit.sourceChains[normalizedSourceChainId].depositTokens[
      depositTokenSymbol
    ].address;

  if (!vault) {
    throw new Error(`Invalid vault key: ${vaultKey}`);
  }

  if (!vault.contracts) {
    throw new Error(`Contracts not configured for vault ${vaultKey}`);
  }

  if (!vault.contracts.accountant) {
    throw new Error(`Accountant contract not configured for vault ${vaultKey}`);
  }

  const bridgeIdentifier =
    vault.deposit.sourceChains[normalizedSourceChainId].destinationChains[
      normalizedDestinationChainId
    ].bridge.chainIdentifier;

  // Calculate minimum mint amount based on rate
  const [depositAssetDecimals, rate] = await getRateInQuoteWithAssetDecimals({
    assetAddress: depositAssetAddress,
    accountantAddress: vault.contracts.accountant,
    chainId: normalizedSourceChainId,
  });

  if (depositAssetDecimals.status === 'failure') {
    throw new Error(depositAssetDecimals.error.message);
  }

  if (rate.status === 'failure') {
    throw new Error(rate.error.message);
  }

  const depositAmountAsBitInt = parseUnits(
    depositAmount,
    depositAssetDecimals.result
  );

  const minimumMint = calculateMinimumMint(
    depositAmountAsBitInt,
    rate.result,
    vault.token.decimals,
    slippage
  );

  // Prepare bridge data
  const bridgeData = prepareBridgeContractArg({
    bridgeChainIdentifier: bridgeIdentifier,
    userAddress,
  });

  const previewFee = await getPreviewFee({
    shareAmount: depositAmountAsBitInt,
    bridgeData: bridgeData,
    contractAddress: vault.contracts.teller,
    chainId: normalizedSourceChainId,
  });

  return {
    abi: TellerAbi,
    address: vault.contracts.teller,
    functionName: 'depositAndBridge',
    args: [depositAssetAddress, depositAmountAsBitInt, minimumMint, bridgeData],
    chainId: normalizedSourceChainId,
    value: previewFee,
  };
};

interface PrepareApproveDepositTokenParams {
  vaultKey: VaultKey;
  depositTokenSymbol: string;
  depositAmount?: string;
  chainId: ChainId;
}

interface ApproveDepositTokenTransactionData {
  abi: typeof erc20Abi;
  address: Address;
  functionName: 'approve';
  args: [Address, bigint];
}

const prepareApproveDepositToken = async ({
  vaultKey,
  depositTokenSymbol,
  depositAmount,
  chainId,
}: PrepareApproveDepositTokenParams): Promise<ApproveDepositTokenTransactionData> => {
  const normalizedChainId = toChainId(chainId);
  const vault = await getVaultByKey(vaultKey);

  if (!vault) {
    throw new Error(`Invalid vault key: ${vaultKey}`);
  }

  if (!vault.contracts) {
    throw new Error(`Contracts not configured for vault ${vaultKey}`);
  }

  if (!vault.contracts.boringVault) {
    throw new Error(`Teller contract not configured for vault ${vaultKey}`);
  }

  const depositAssetAddress =
    vault.deposit.sourceChains[normalizedChainId].depositTokens[
      depositTokenSymbol
    ].address;

  if (!depositAssetAddress) {
    throw new Error(`Invalid deposit token symbol: ${depositTokenSymbol}`);
  }

  const {
    data: depositAssetDecimals,
    error: depositAssetDecimalsError,
    success: depositAssetDecimalsSuccess,
  } = await tryCatch(
    getErc20Decimals({
      chainId: normalizedChainId,
      tokenAddress: depositAssetAddress,
    })
  );

  if (depositAssetDecimalsSuccess === false) {
    throw new Error(
      `Error getting deposit asset decimals. ${depositAssetDecimalsError.name}: ${depositAssetDecimalsError.message}`
    );
  }

  if (!depositAssetDecimals) {
    throw new Error(`Invalid deposit token symbol: ${depositTokenSymbol}`);
  }

  const depositAmountAsBigInt = depositAmount
    ? parseUnits(depositAmount, depositAssetDecimals)
    : DEFAULT_APPROVAL_AMOUNT;

  return {
    abi: erc20Abi,
    address: depositAssetAddress,
    functionName: 'approve',
    args: [vault.contracts.boringVault, depositAmountAsBigInt],
  };
};

export {
  prepareApproveDepositToken,
  prepareDepositAndBridgeTransactionData,
  prepareDepositTransactionData,
};

export type {
  ApproveDepositTokenTransactionData,
  DepositAndBridgeTransactionData,
  DepositTransactionData,
};
